import {
  NOTIFICATION_TYPES,
  DRAWER_PLACEMENTS,
} from "../../../../../config/constants";
import { useEffect, useState } from "react";
import {
  Button,
  Checkbox,
  Drawer,
  Empty,
  Form,
  Input,
  InputNumber,
  Radio,
  RadioChangeEvent,
  Space,
  TablePaginationConfig,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { EditOutlined, CloseOutlined, CheckOutlined } from "@ant-design/icons";
import MappingService from "../../../../../services/mappings/MappingService";
import TablesService from "../../../../../services/tables/TablesService";
import { GetTablesRequest } from "../../../../../models/tables/GetTablesRequest";
import { UpdateMigrationRuleMapping } from "../../../../../models/migrationrules/UpdateMigrationRuleMapping";
import { useTranslation } from "react-i18next";
import NotificationsUtil from "../../../../shared/utils/NotificationsUtil";
import Datatable from "../../../../shared/components/Datatable";

const { Search } = Input;

enum RowAction {
  View = 1,
  Edit,
  Create,
}

const RuleOverview = ({
  selectedEntity,
  selectedGroupCode,
  newRuleOverviewModalEnabled,
  triggerUpdate,
  setTriggerUpdate,
  entities,
}: {
  selectedEntity: any;
  selectedGroupCode: any;
  setNewRuleOverviewModalEnabled: (val: boolean) => void;
  newRuleOverviewModalEnabled: boolean;
  triggerUpdate: boolean;
  setTriggerUpdate: any;
  entities: any;
}) => {
  const { t, i18n } = useTranslation("translations");
  interface DataType {
    fromTableTrimmed: any;
    id: number;
    key: string;
    ruleName: string;
    join: string;
    valueColumn: string;
    conditionValue: string;
    weight: number;
    migrationStatus: string;
    isActive: boolean;
  }

  type DataIndex = keyof DataType;

  const [data, setData] = useState<DataType[]>([]);

  const [drawerAction, setDrawerAction] = useState<RowAction>();
  const [drawerTitle, setDrawerTitle] = useState<string>("");
  const [openDrawer, setOpenDrawer] = useState(false);
  const [loadingButton, setLoadingButton] = useState(false);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [ruleNameInput, setRuleNameInput] = useState("");
  const [weightInput, setWeightInput] = useState<any>(0);
  const [migrationStatus, setMigrationStatus] = useState<any>(1);
  const [isActive, setIsActive] = useState(false);
  const [joinInput, setJoinInput] = useState("");
  const [valueInput, setValueInput] = useState("");
  const [conditionValueInput, setConditionValueInput] = useState("");
  const [editedRow, setEditedRow] = useState<any>();
  const [updateRowAllowed, setUpdateRowAllowed] = useState(false);

  const [destinationTable, setDestinationTable] = useState<any>();
  const [predecessorEntity, setPredecessorEntity] = useState("");

  useEffect(() => {
    if (destinationTable) {
      setIsTableLoading(true);
      MappingService.getAll(selectedGroupCode ?? null, entities.find(x => x.id === selectedEntity)?.name).then((response: any) => {
        const responseData = response.data
          .map((x: any) => ({
            ...x,
            table: x.object,
            ruleId: x.rule_Id,
            ruleName: x.rule_Name,
            fromTableTrimmed: x.fromTable?.split(".")[2],
          }));
        setData(responseData);
        setIsTableLoading(false);
      });
    }
  }, [destinationTable, selectedGroupCode, triggerUpdate]);

  useEffect(() => {
    if (newRuleOverviewModalEnabled) {
      showDrawer(RowAction.Create);
    }
  }, [newRuleOverviewModalEnabled]);

  useEffect(() => {
    if (!selectedEntity) return;

    const toModel: GetTablesRequest = {
      entityId: selectedEntity,
      operationId: 2,
      groupCode: "",
    };

    TablesService.getTables(toModel).then((response) => {
      const data = response.data;
      if (data && data !== undefined && data.length > 0) {
        setDestinationTable(data[0]);
      }
    });
  }, [selectedEntity]);

  const showDrawer = (action: RowAction) => {
    setDrawerAction(action);
    switch (action) {
      case RowAction.View:
        setDrawerTitle(
          t(
            "Page.Operation.MigrationRules.Tabs.RuleOverview.Labels.RowDetails",
            {
              lngs: i18n.languages,
              defaultValue: "Row details",
            }
          )!
        );
        break;
      case RowAction.Edit:
        setDrawerTitle(
          t("Page.Operation.MigrationRules.Tabs.RuleOverview.Labels.EditRule", {
            lngs: i18n.languages,
            defaultValue: "Edit rule",
          })!
        );
        break;
      default:
        break;
    }
    setOpenDrawer(true);
  };

  const onEditRule = (record: any) => {
    showDrawer(RowAction.Edit);
    setRuleNameInput(record.ruleName);
    setWeightInput(record.weight);
    setMigrationStatus(record.migrationStatus === "MIGRATION" ? 1 : 2);
    setIsActive(record.isActive);
    setJoinInput(record.join);
    setValueInput(record.valueColumn);
    setConditionValueInput(record.conditionValue);
    setEditedRow(record);
    setPredecessorEntity(
      entities.find((x: any) => x.id === record.predecessorEntityId)?.name
    );
  };

  const onDeleteRule = (id: number) => {
    MappingService.remove(id)
      .then((response: any) => {
        setTriggerUpdate(!triggerUpdate);
        NotificationsUtil.openNotificationDialog({
          description: t(
            "Page.Operation.MigrationRules.Tabs.RuleOverview.Notifications.Rule.DeleteSuccessDescription",
            {
              lngs: i18n.languages,
              defaultValue: "Rule deleted successfully.",
            }
          )!,
          type: NOTIFICATION_TYPES.SUCCESS,
          logError: false,
        });
      })
      .catch((error: any) => {
        NotificationsUtil.openNotificationDialog({
          description: error.response.data,
          type: NOTIFICATION_TYPES.ERROR,
          logError: true,
        });
      });
  };

  const onDoubleClickRow = (record: any) => {
    setRuleNameInput(record.ruleName);
    setWeightInput(record.weight);
    setMigrationStatus(record.migrationStatus === "MIGRATION" ? 1 : 2);
    setIsActive(record.isActive);
    setJoinInput(record.join);
    setValueInput(record.valueColumn);
    setConditionValueInput(record.conditionValue);
    setEditedRow(record);
    showDrawer(RowAction.View);
    setPredecessorEntity(
      entities.find((x: any) => x.id === record.predecessorEntityId).name
    );
  };

  const onClose = () => {
    setOpenDrawer(false);
    setRuleNameInput("");
    setJoinInput("");
    setValueInput("");
    setConditionValueInput("");
    setWeightInput(0);
    setMigrationStatus(1);
    setIsActive(false);
    setEditedRow(null);
    setPredecessorEntity("");
  };

  const onWeightInputChange = (value: number) => {
    setWeightInput(value);
  };

  const onMigrationStatusChange = (e: RadioChangeEvent) => {
    setMigrationStatus(e.target.value);
  };

  useEffect(() => {
    if (editedRow && (weightInput || migrationStatus || isActive)) {
      const def = data.find((x: any) => x.id === editedRow.id);
      const defMigrationStatus = def?.migrationStatus === "MIGRATION" ? 1 : 2;
      if (weightInput !== def?.weight) {
        setUpdateRowAllowed(true);
      } else if (migrationStatus !== defMigrationStatus) {
        setUpdateRowAllowed(true);
      } else if (isActive !== def?.isActive) {
        setUpdateRowAllowed(true);
      } else {
        setUpdateRowAllowed(false);
      }
    } else {
      setUpdateRowAllowed(false);
    }
  }, [weightInput, migrationStatus, isActive, editedRow]);

  const onSaveRule = () => {
    if (editedRow) {
      const model: UpdateMigrationRuleMapping = {
        mappingId: editedRow.id,
        fromTableId: editedRow.fromTableId,
        groupCode: editedRow.groupCode,
        masterId: editedRow.masterId,
        conditionId: editedRow.conditionId,
        weight: weightInput,
        migrationStatus: migrationStatus === 1 ? "MIGRATION" : "NO MIGRATION",
        environment: "",
        isActive: isActive,
      };

      MappingService.update(model)
        .then((response: any) => {
          setTriggerUpdate(!triggerUpdate);
          onClose();
          NotificationsUtil.openNotificationDialog({
            description: t(
              "Page.Operation.MigrationRules.Tabs.RuleOverview.Notifications.Rule.UpdateSuccessDescription",
              {
                lngs: i18n.languages,
                defaultValue: "Rule edited successfully.",
              }
            )!,
            type: NOTIFICATION_TYPES.SUCCESS,
            logError: false,
          });
        })
        .catch((error: any) => {
          NotificationsUtil.openNotificationDialog({
            description: error.response.data,
            type: NOTIFICATION_TYPES.ERROR,
            logError: true,
          });
        });
    }
  };

  const columns: ColumnsType<DataType> = [
    {
      title: t(
        "Page.Operation.MigrationRules.Tabs.RuleOverview.Table.Headers.ImportTable",
        {
          lngs: i18n.languages,
          defaultValue: "Import Table",
        }
      ),
      dataIndex: "fromTableTrimmed",
      key: "fromTableTrimmed",
      width: "150px",
      filterMode: "tree",
      filterSearch: true,
      sorter: (a, b) => a.fromTableTrimmed ? a.fromTableTrimmed.localeCompare(b.fromTableTrimmed ?? "") : "".localeCompare(b.fromTableTrimmed ?? ""),
    },
    {
      title: t(
        "Page.Operation.MigrationRules.Tabs.RuleOverview.Table.Headers.RuleName",
        {
          lngs: i18n.languages,
          defaultValue: "Rule Name",
        }
      ),
      dataIndex: "ruleName",
      key: "ruleName",
      width: "200px",
      filters: [
        {
          text: "Default",
          value: "Default",
        },
        {
          text: "Removed from source",
          value: "Removed from source",
        },
      ],
      filterMode: "tree",
      filterSearch: true,
      sorter: (a, b) => a.ruleName ? a.ruleName.localeCompare(b.ruleName ?? "") : "".localeCompare(b.ruleName ?? ""),
    },
    {
      title: t(
        "Page.Operation.MigrationRules.Tabs.RuleOverview.Table.Headers.Join",
        {
          lngs: i18n.languages,
          defaultValue: "Join",
        }
      ),
      dataIndex: "join",
      key: "join",
      width: "200px",
      filters: [
        {
          text: "Pop",
          value: "Pop",
        },
      ],
      filterMode: "tree",
      filterSearch: true,
      sorter: (a, b) => a.join ? a.join.localeCompare(b.join ?? "") : "".localeCompare(b.join ?? ""),
      ellipsis: true,
    },
    {
      title: t(
        "Page.Operation.MigrationRules.Tabs.RuleOverview.Table.Headers.Value",
        {
          lngs: i18n.languages,
          defaultValue: "Value",
        }
      ),
      dataIndex: "valueColumn",
      key: "valueColumn",
      filters: [
        {
          text: "Pop.Code",
          value: "Pop.Code",
        },
        {
          text: "ISNULL(Pop.Stock, 0)	",
          value: "ISNULL(Pop.Stock, 0)	",
        },
        {
          text: "pop.OldMaterial	",
          value: "pop.OldMaterial	",
        },
      ],
      filterMode: "tree",
      filterSearch: true,
      sorter: (a, b) => a.valueColumn ? a.valueColumn.localeCompare(b.valueColumn ?? "") : "".localeCompare(b.valueColumn ?? ""),
      ellipsis: true,
    },
    {
      title: t(
        "Page.Operation.MigrationRules.Tabs.RuleOverview.Table.Headers.ConditionValue",
        {
          lngs: i18n.languages,
          defaultValue: "Condition Value",
        }
      ),
      key: "conditionValue",
      dataIndex: "conditionValue",
      filters: [
        {
          text: "Pop.Code",
          value: "Pop.Code",
        },
        {
          text: "ISNULL(Pop.Stock, 0)	",
          value: "ISNULL(Pop.Stock, 0)	",
        },
        {
          text: "ISNULL(Result.Value, '') <>",
          value: "ISNULL(Result.Value, '') <>",
        },
      ],
      filterMode: "tree",
      filterSearch: true,
      sorter: (a, b) => a.conditionValue ? a.conditionValue.localeCompare(b.conditionValue ?? "") : "".localeCompare(b.conditionValue ?? ""),
      ellipsis: true,
    },
    {
      title: t(
        "Page.Operation.MigrationRules.Tabs.RuleOverview.Table.Headers.Weight",
        {
          lngs: i18n.languages,
          defaultValue: "Weight",
        }
      ),
      key: "weight",
      dataIndex: "weight",
      filters: [
        {
          text: "10",
          value: "10",
        },
        {
          text: "1000",
          value: "1000",
        },
        {
          text: "15",
          value: "15",
        },
      ],
      filterMode: "tree",
      filterSearch: true,
      sortOrder: "ascend",
      sorter: (a, b) => (a.weight ?? 0) - (b.weight ?? 0),
    },
    {
      title: t(
        "Page.Operation.MigrationRules.Tabs.RuleOverview.Table.Headers.MigrationStatus",
        {
          lngs: i18n.languages,
          defaultValue: "Migration status",
        }
      ),
      key: "migrationStatus",
      dataIndex: "migrationStatus",
      filters: [],
      filterMode: "tree",
      sorter: (a, b) => a.migrationStatus ? a.migrationStatus.localeCompare(b.migrationStatus ?? "") : "".localeCompare(b.migrationStatus ?? ""),
    },
    {
      title: t(
        "Page.Operation.MigrationRules.Tabs.RuleOverview.Table.Headers.IsActive",
        {
          lngs: i18n.languages,
          defaultValue: "Active",
        }
      ),
      key: "isActive",
      dataIndex: "isActive",
      width: "100px",
      align: "center",
      // render: (text: any, record: any) => (
      //   <>
      //     <Checkbox checked={record.isActive}></Checkbox>
      //   </>
      // ),
      filterMode: "tree",
      filterSearch: true,
    },
  ];

  const scroll = { y: "calc(100vh - 330px)" };

  return (
    <>
      {!selectedEntity ? (
        <Empty
          style={{ display: "flex", width: "100%" }}
          description={t(
            "Page.Operation.MigrationRules.Tabs.RuleOverview.Tutorial.GetStarted",
            {
              lngs: i18n.languages,
              defaultValue:
                "Please select an Entity to get started",
            }
          )}
        ></Empty>
      ) : (
        <Datatable
          scroll={scroll}
          tableColumns={columns}
          data={data}
          setData={setData}
          isTableLoading={isTableLoading}
          otherActions={[]}
          otherRowEnabled={false}
          editRowEnabled={true}
          copyRowEnabled={false}
          deleteRowEnabled={true}
          onEditFunc={onEditRule}
          onCopyFunc={undefined}
          onDeleteFunc={onDeleteRule}
          onDoubleClickRow={onDoubleClickRow}
          defaultSortColumn={"weight"}
          defaultSortColumnDirection={"ascend"}
          checkPermissions={true}
          paginationDisabled={true}
        />
      )}
      <Drawer
        width={"400px"}
        getContainer={false}
        style={{ position: "absolute" }}
        title={drawerTitle}
        placement={DRAWER_PLACEMENTS.BOTTOM}
        onClose={onClose}
        visible={openDrawer}
        closable={false}
        height={"550px"}
        extra={
          <Space>
            {RowAction?.View === drawerAction && (
              <>
                <Button
                  onClick={() => showDrawer(RowAction.Edit)}
                  icon={<EditOutlined />}
                  className={"active-btn"}
                  size="small"
                >
                  {t("General.Labels.Edit", {
                    lngs: i18n.languages,
                    defaultValue: "Edit",
                  })}
                </Button>
                <Button
                  type="ghost"
                  onClick={onClose}
                  icon={<CloseOutlined />}
                  size="small"
                />
              </>
            )}
            {RowAction?.Edit === drawerAction && (
              <>
                <Button
                  disabled={!updateRowAllowed}
                  className={!updateRowAllowed ? "" : "active-btn"}
                  onClick={onSaveRule}
                  icon={<CheckOutlined />}
                  size="small"
                  loading={loadingButton}
                >
                  {t("General.Labels.Update", {
                    lngs: i18n.languages,
                    defaultValue: "Update",
                  })}
                </Button>
                <Button
                  type="ghost"
                  onClick={onClose}
                  icon={<CloseOutlined />}
                  size="small"
                />
              </>
            )}
          </Space>
        }
      >
        <Form
          disabled={RowAction.View === drawerAction ? true : false}
          layout="vertical"
        >
          <div className="form-top">
            <Form.Item
              label={t(
                "Page.Operation.MigrationRules.Tabs.RuleOverview.Labels.RuleName",
                {
                  lngs: i18n.languages,
                  defaultValue: "Rule Name",
                }
              )}
              tooltip={t(
                "Page.Operation.MigrationRules.Tabs.RuleOverview.Tooltips.RuleName",
                {
                  lngs: i18n.languages,
                  defaultValue: "",
                }
              )}
            >
              <Input
                disabled
                allowClear
                value={ruleNameInput}
                placeholder={
                  t("General.Placeholders.EnterValue", {
                    lngs: i18n.languages,
                    defaultValue: "Enter value",
                  })!
                }
              ></Input>
            </Form.Item>
            <Form.Item
              label={t(
                "Page.Operation.MigrationRules.Tabs.RuleOverview.Labels.Weight",
                {
                  lngs: i18n.languages,
                  defaultValue: "Weight",
                }
              )}
              tooltip={t(
                "Page.Operation.MigrationRules.Tabs.RuleOverview.Tooltips.Weight",
                {
                  lngs: i18n.languages,
                  defaultValue: "",
                }
              )}
            >
              <InputNumber
                min={1}
                value={weightInput}
                placeholder={
                  t("General.Placeholders.EnterValue", {
                    lngs: i18n.languages,
                    defaultValue: "Enter value",
                  })!
                }
                onChange={onWeightInputChange}
              ></InputNumber>
            </Form.Item>
            <Form.Item
              label={t(
                "Page.Operation.MigrationRules.Tabs.RuleOverview.Labels.PredecessorEntity",
                {
                  lngs: i18n.languages,
                  defaultValue: "Predecessor Entity:",
                }
              )}
              tooltip={t(
                "Page.Operation.MigrationRules.Tabs.RuleOverview.Tooltips.PredecessorEntity",
                {
                  lngs: i18n.languages,
                  defaultValue: "",
                }
              )}
            >
              <Input
                disabled
                allowClear
                value={predecessorEntity}
                placeholder={
                  t("General.Placeholders.", {
                    lngs: i18n.languages,
                    defaultValue: "",
                  })!
                }
              ></Input>
            </Form.Item>
            <Form.Item
              label={t(
                "Page.Operation.MigrationRules.Tabs.RuleOverview.Labels.MigrationStatus",
                {
                  lngs: i18n.languages,
                  defaultValue: "Migration status",
                }
              )}
              tooltip={t(
                "Page.Operation.MigrationRules.Tabs.RuleOverview.Tooltips.MigrationStatus",
                {
                  lngs: i18n.languages,
                  defaultValue: "",
                }
              )}
            >
              <Radio.Group
                onChange={onMigrationStatusChange}
                value={migrationStatus}
              >
                <Radio value={1}>
                  {t(
                    "Page.Operation.MigrationRules.Tabs.RuleOverview.Labels.Migration",
                    {
                      lngs: i18n.languages,
                      defaultValue: "Migration",
                    }
                  )}
                </Radio>
                <Radio value={2}>
                  {" "}
                  {t(
                    "Page.Operation.MigrationRules.Tabs.RuleOverview.Labels.NoMigration",
                    {
                      lngs: i18n.languages,
                      defaultValue: "No Migration",
                    }
                  )}
                </Radio>
              </Radio.Group>
            </Form.Item>
            <Form.Item
              label={t(
                "Page.Operation.MigrationRules.Tabs.RuleOverview.Labels.IsActive",
                {
                  lngs: i18n.languages,
                  defaultValue: "Is Active",
                }
              )}
              tooltip={t(
                "Page.Operation.MigrationRules.Tabs.RuleOverview.Tooltips.IsActive",
                {
                  lngs: i18n.languages,
                  defaultValue: "",
                }
              )}
            >
              <Checkbox
                checked={isActive}
                onClick={() => setIsActive(!isActive)}
              >
                {
                  t(
                    "Page.Operation.MigrationRules.Tabs.RuleOverview.Table.Headers.IsActive",
                    {
                      lngs: i18n.languages,
                      defaultValue: "Is Active",
                    }
                  )!
                }
              </Checkbox>
            </Form.Item>
          </div>
          <Form.Item
            label={t(
              "Page.Operation.MigrationRules.Tabs.RuleOverview.Labels.Join",
              {
                lngs: i18n.languages,
                defaultValue: "Join",
              }
            )}
            tooltip={t(
              "Page.Operation.MigrationRules.Tabs.RuleOverview.Tooltips.Join",
              {
                lngs: i18n.languages,
                defaultValue: "",
              }
            )}
          >
            <Input.TextArea
              disabled
              allowClear
              rows={4}
              value={joinInput}
              placeholder={
                t("General.Placeholders.EnterValue", {
                  lngs: i18n.languages,
                  defaultValue: "Enter value",
                })!
              }
            // onChange={onJoinInputChange}
            ></Input.TextArea>
          </Form.Item>
          <Form.Item
            label={t(
              "Page.Operation.MigrationRules.Tabs.RuleOverview.Labels.Value",
              {
                lngs: i18n.languages,
                defaultValue: "Value",
              }
            )}
            tooltip={t(
              "Page.Operation.MigrationRules.Tabs.RuleOverview.Tooltips.Value",
              {
                lngs: i18n.languages,
                defaultValue: "",
              }
            )}
          >
            <Input.TextArea
              disabled
              allowClear
              rows={4}
              value={valueInput}
              placeholder={
                t("General.Placeholders.EnterValue", {
                  lngs: i18n.languages,
                  defaultValue: "Enter value",
                })!
              }
            // onChange={onValueInputChange}
            ></Input.TextArea>
          </Form.Item>
          <Form.Item
            label={t(
              "Page.Operation.MigrationRules.Tabs.RuleOverview.Labels.ConditionValue",
              {
                lngs: i18n.languages,
                defaultValue: "Condition value",
              }
            )}
            tooltip={t(
              "Page.Operation.MigrationRules.Tabs.RuleOverview.Tooltips.ConditionValue",
              {
                lngs: i18n.languages,
                defaultValue: "",
              }
            )}
          >
            <Input.TextArea
              disabled
              allowClear
              rows={4}
              value={conditionValueInput}
              placeholder={
                t("General.Placeholders.EnterValue", {
                  lngs: i18n.languages,
                  defaultValue: "Enter value",
                })!
              }
            // onChange={onValueInputChange}
            ></Input.TextArea>
          </Form.Item>
        </Form>
      </Drawer>
    </>
  );
};

export default RuleOverview;
